import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(advancedFormat);

export const formatDate = (date: string, format: string = 'DD-MM-YYYY HH:mm', hour?: number, minute?: number): string => {
  if ((hour || hour === 0) && (minute || minute === 0)) {
    return dayjs(date).hour(hour).minute(minute).format(format);
  }
  return dayjs(date).format(format);
};

export const formatDateWithTimeZone = (date: string, format: string = 'DD-MM-YYYY HH:mm') => {
  return dayjs.tz(date, 'Europe/Amsterdam').format(format);
};

//use this if data is GMT
//pass date, convert it to UTC and get it in given timezone
export const formatDateWithUtcInTimeZone = (date: string, timeZone: string = 'Europe/Amsterdam', format: string = 'DD-MM-YYYY HH:mm') => {
  return dayjs.utc(date).tz(timeZone).format(format);
};

export const getTimeZoneShortNameAndOffset = (timeZoneLongForm: string) => {
  if (!timeZoneLongForm) {
    return timeZoneLongForm;
  }
  const abbr = dayjs().tz(timeZoneLongForm, true).format('z');
  const offset = dayjs().tz(timeZoneLongForm).utcOffset() / 60;

  if (abbr.match('GMT')) {
    return `(${abbr})`;
  }
  return `${abbr} (GMT${offset < 0 ? offset : `+${offset}`})`;
};

export const getDateInLocalTimeZone = (date: string) => {
  return dayjs.tz(date, 'Etc/GMT').local();
};
// use this method if date in utc
export const formatDateInLocalTimeZone = (date: string, format: string = 'DD-MM-YYYY HH:mm') => {
  return dayjs.tz(date, 'Etc/GMT').local().format(format);
};

// use this method if date in GTM,
// pass date and timezone, convert to utc format, then get in user local timezone
export const formatDateWithUtcInLocalTimeZone = (date: string, timezone: string) => {
  const utc = dayjs.tz(date, timezone).utc().format();
  return formatDateInLocalTimeZone(utc);
};

export const futureDateInTimeUnit = (date: string, timeZone: string, unit: any) => {
  return dayjs.tz(date, timeZone).local().diff(dayjs(), unit);
};

export const formatPositions = (position: string): any => {
  if (!position) {
    return null;
  }

  const items = position.split(' ');
  return {
    lat: Number(items[0]),
    lng: Number(items[1]),
  };
};

export const getHours = (data: string = ''): number | string => {
  const hour = dayjs(data).hour();
  if (hour) {
    return hour;
  } else {
    if (data.includes('T')) {
      const time = data.split('T')[1];
      const parts = time.split(':');
      return parts[0];
    } else {
      const parts = data.split(':');
      return parts[0];
    }
  }
};

export const getMinutes = (data: string = ''): number | string => {
  const minute = dayjs(data).minute();
  if (minute) {
    return minute;
  } else {
    if (data.includes('T')) {
      const time = data.split('T')[1];
      const parts = time.split(':');
      return parts[1];
    } else {
      const parts = data.split(':');
      return parts[1];
    }
  }
};

export const getDate = (date: string, format?: string): string => dayjs(date).format(format || 'YYYY-MM-DD');

export const getDateDiff = (from: any, to: any = dayjs(), unit: any = 'day') => {
  return dayjs(from).diff(to, unit);
};

export const getCurrentDate = () => {
  return dayjs();
};

export const isBeforeDate = (date: string) => {
  return dayjs().isBefore(dayjs.tz(date, 'Etc/GMT').local());
};

export const clearCache = async (cache: any, queryName: any, selector: any, withResults: boolean = true) => {
  return cache.modify({
    fields: {
      [selector](existingItems: any) {
        let newData: any;
        if (withResults) {
          newData = cache.writeQuery({
            query: queryName,
            data: {
              [selector]: {
                results: [],
                pagination: [],
              }
            }
          });
        } else {
          newData = cache.writeQuery({
            query: queryName,
            data: {
              [selector]: null
            }
          });
        }
        const results = existingItems?.results?.length ? existingItems.results : [];
        return [...results, newData];
      }
    }
  });
};

export const removeItemFromCache = (cache: any, selector: any, id: number) => {
  return cache.modify({
    fields: {
      [selector](existingItems: any, { readField }: any) {
        return existingItems.filter((ref: any) => readField('id', ref) !== id);
      }
    }
  });
};

export const replaceUnderlineWithSpace = (text: string) => {
  const newText = text.toLowerCase().replace(/_/g, " ");
  return newText.charAt(0).toUpperCase() + newText.slice(1);
};

export const isObject = (obj: any) => {
  if (typeof obj === 'object' && obj !== null && !Array.isArray(obj)) {
    return true;
  }
  return false;
};

export const formatTime = (time: string) => {
  return time?.length === 1 ? `0${time}` : time;
};

export const trimTextString = (text: string, characterCount: number) => {
  return text.length < characterCount ? text : `${text.substring(0, characterCount)}...`;
};
